import { ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { FaqService, StorageService } from '@coin/shared/core/data-access';
import { ContentLanguage, StorageKey } from '@coin/shared/core/util-enums';
import { FaqItem } from '@coin/shared/core/util-models';
import { TranslateService } from '@ngx-translate/core';
import { filter, finalize, iif, map, of, switchMap, take, tap, timer } from 'rxjs';
import { ChatFaqMenuComponent } from '../chat-faq-menu/chat-faq-menu.component';

@Component({
  selector: 'coin-chat-faq-screen',
  templateUrl: './chat-faq-screen.component.html',
  styleUrl: './chat-faq-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, ChatFaqMenuComponent]
})
export class ChatFaqScreenComponent {
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly faqService = inject(FaqService);
  private readonly storageService = inject(StorageService);

  faqMenu = viewChild(ChatFaqMenuComponent);

  public languageSelected = ContentLanguage.ENGLISH;
  public backPress = output<void>();
  public isHelpDataLoading = signal(false);
  // FAQ
  key = 'coin-ai';
  public helpData: { [lang in ContentLanguage]?: FaqItem[] } = {};

  constructor() {
    this.initFaq();
  }

  private async initFaq() {
    this.languageSelected = await this.storageService.get(StorageKey.LANGUAGE);
    await this.loadHelpData();

    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      this.languageSelected = lang.lang as ContentLanguage;
      this.initLoadHelpData();
    });
  }

  private initLoadHelpData(): void {
    // Adding a small delay to avoid flickering when switching languages fast
    timer(600)
      .pipe(
        tap(() => this.loadHelpData()),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private async loadHelpData(): Promise<void> {
    if (!this.helpData[this.languageSelected]) {
      this.isHelpDataLoading.set(true);

      this.faqService
        .getFaq(`help_${this.languageSelected}_publish.json`, this.key)
        .pipe(
          switchMap(data => iif(() => !data, this.faqService.getFaq(`help_en_publish.json`, this.key), of(data))),
          filter(helpItem => !!helpItem),
          map(helpItems => {
            // Only show items that are meant for this app
            return helpItems.filter(item => item.visibility?.value === this.key);
          }),
          tap(helpItems => {
            this.searchForNewFlagItems(helpItems);
            this.helpData[this.languageSelected] = helpItems;
          }),
          finalize(() => {
            this.isHelpDataLoading.set(false);
          })
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  private searchForNewFlagItems(helpItems: FaqItem[]): void {
    for (const item of helpItems) {
      item.newFlagCount = this.checkItemsForFlag(item);
    }
  }

  private checkItemsForFlag(item: FaqItem): number {
    if (item && item.content && !item.isAnswer) {
      let contentNewFlagCount = 0;
      if (item.newFlagUntil) {
        contentNewFlagCount += 1;
      } else {
        for (const itm of item.content) {
          itm.newFlagCount = this.checkItemsForFlag(itm);
          contentNewFlagCount += itm.newFlagCount;
        }
      }
      return contentNewFlagCount;
    }
    if (item && item.content && item.isAnswer) {
      if (item.newFlagUntil) {
        const timeUntilEndNewFlag =
          new Date(item.newFlagUntil).getTime() - new Date(new Date().setHours(0, 0, 0, 0)).getTime();
        if (timeUntilEndNewFlag > 0) {
          return 1;
        }
      }
    }
    return 0;
  }

  onBackPress() {
    const faqMenuComponent = this.faqMenu();
    const generalTabIndex = faqMenuComponent?.generalTabIndex();
    const faqTabIndex = faqMenuComponent?.faqTabIndex();

    // in case both indexes are 0, emit back press to parent component
    if (faqTabIndex === 0 && generalTabIndex === 0) {
      this.backPress.emit();
      return;
    }

    // else there is a page to go back to
    faqMenuComponent?.reduceOnePage();
  }
}
